import { createContext, useContext } from 'react';
import type { User } from '@/types/auth';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName: string | undefined,
    role: 'coach' | 'athlete'
  ) => Promise<void>;
  /** Async logout: POSTs to /api/auth/logout so the backend clears the httpOnly
   *  refresh-token cookie, then clears local state and navigates to /login.
   *  Safe to call without awaiting — always completes even on network failure. */
  logout: () => Promise<void>;
  /** Re-fetch the current user from the server and update context state. */
  refreshUser: () => Promise<void>;
  /**
   * Sign in with a Google ID token credential (GSI Option A — existing users only).
   * Mirrors the `login` method: stores tokens and sets the user in context.
   * Re-throws the AxiosError on failure so the caller can branch on the error code.
   */
  loginWithGoogle: (credential: string) => Promise<void>;
}

// Exported so AuthProvider.tsx (react-refresh/only-export-components split —
// see that file's comment) can construct the Provider.
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
